<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
  deleteMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';

const props = defineProps({
  channel: { type: String, required: true },
});

const route = useRoute();
const accountId = route.params.accountId;

const TITLES = { ticimax: 'Ticimax', shopify: 'Shopify' };
const CATEGORIES = [
  { v: 'altyapi', l: 'Altyapı (yıllık)' },
  { v: 'reklam', l: 'Reklam' },
  { v: 'kargo', l: 'Kargo' },
  { v: 'diger', l: 'Diğer' },
];
// Kırılım gridinde gösterilen kategoriler (kapıda ödeme komisyonu Aras'tan otomatik
// gelir, manuel forma eklenmez — o yüzden ayrı liste).
const BREAKDOWN_CATEGORIES = [
  { v: 'altyapi', l: 'Altyapı (yıllık)' },
  { v: 'reklam', l: 'Reklam' },
  { v: 'kargo', l: 'Kargo (taşıma)' },
  { v: 'kapida_komisyon', l: 'Kapıda Ödeme Komisyonu' },
  { v: 'diger', l: 'Diğer' },
];

// Kargo/sipariş durumu: kod → etiket + renk sınıfı (rozet). Canlı Aras kodları + türetilenler.
const CARGO_STATUS = {
  delivered: {
    l: 'Teslim Edildi',
    c: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  },
  out_for_delivery: {
    l: 'Dağıtımda',
    c: 'bg-woot-100 text-woot-800 dark:bg-woot-900 dark:text-woot-300',
  },
  at_branch: {
    l: 'Şubede',
    c: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  },
  in_transit: {
    l: 'Yolda',
    c: 'bg-woot-100 text-woot-800 dark:bg-woot-900 dark:text-woot-300',
  },
  shipped: {
    l: 'Kargoya Verildi',
    c: 'bg-woot-100 text-woot-800 dark:bg-woot-900 dark:text-woot-300',
  },
  failed: {
    l: 'Teslim Edilemedi',
    c: 'bg-ruby-100 text-ruby-800 dark:bg-ruby-900 dark:text-ruby-300',
  },
  returned: {
    l: 'İade',
    c: 'bg-ruby-100 text-ruby-800 dark:bg-ruby-900 dark:text-ruby-300',
  },
  cancelled: { l: 'İptal', c: 'bg-n-slate-4 text-n-slate-11' },
  awaiting_shipment: {
    l: 'Kargo Bekleniyor',
    c: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300',
  },
  no_data: { l: 'Aras Kaydı Yok', c: 'bg-n-slate-3 text-n-slate-10' },
  payment_missing: {
    l: 'Ödeme Kaydı Yok',
    c: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  },
};
// Filtre menüsünde gösterilecek durumlar.
const STATUS_FILTERS = [
  'delivered',
  'in_transit',
  'out_for_delivery',
  'at_branch',
  'failed',
  'returned',
  'awaiting_shipment',
  'cancelled',
  'payment_missing',
  'no_data',
];
const cargoStatus = code => {
  const s = CARGO_STATUS[code];
  if (!s) return null;
  // "Aras Kaydı Yok" etiketini kanalın taşıyıcısına göre uyarla (Shopify → Sürat).
  if (code === 'no_data') return { ...s, l: `${carrier.value} Kaydı Yok` };
  return s;
};

const overview = ref({});
const expenses = ref([]);
const orders = ref([]);
const summary = ref({}); // filtrelenmiş listenin toplam raporu (katman üst kartları)
const returnsMeta = ref({}); // iade verisi tazeliği (Sürat raporu geriden gelir)
const pagination = ref({ page: 1, total_pages: 1 });
const loading = ref(true);

// Sipariş listesi kolon filtreleri (sunucu tarafı, tüm sayfalarda geçerli).
// Her biri ilgili kolon BAŞLIĞINDAKİ filtre ikonuna tıklanınca açılan menüden ayarlanır.
const filterStatus = ref('');
const filterPayment = ref('');
const filterOrderNo = ref('');
const filterCustomer = ref('');
const openSection = ref('orders');

// Hangi kolon başlığının filtre menüsü açık (null = hiçbiri).
const activeHeaderFilter = ref(null);
const toggleHeaderFilter = col => {
  activeHeaderFilter.value = activeHeaderFilter.value === col ? null : col;
};

const datePreset = ref('all');
const customStart = ref('');
const customEnd = ref('');
const DATE_PRESETS = [
  { v: 'all', l: 'Tüm 2026' },
  { v: 'thismonth', l: 'Bu Ay' },
  { v: 'lastmonth', l: 'Geçen Ay' },
  { v: '30g', l: 'Son 30G' },
  { v: '90g', l: 'Son 90G' },
  { v: 'custom', l: 'Özel' },
];

const anyOrderFilter = computed(
  () =>
    filterStatus.value ||
    filterPayment.value ||
    filterOrderNo.value ||
    filterCustomer.value ||
    datePreset.value !== 'all'
);
const clearOrderFilters = () => {
  filterStatus.value = '';
  filterPayment.value = '';
  filterOrderNo.value = '';
  filterCustomer.value = '';
  datePreset.value = 'all';
  activeHeaderFilter.value = null;
};

const isoDay = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
const computeRange = () => {
  const today = new Date();
  switch (datePreset.value) {
    case 'custom':
      return { start: customStart.value, end: customEnd.value };
    case 'thismonth':
      return {
        start: isoDay(new Date(today.getFullYear(), today.getMonth(), 1)),
        end: isoDay(today),
      };
    case 'lastmonth':
      return {
        start: isoDay(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        end: isoDay(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case '30g':
    case '90g': {
      const s = new Date(today);
      s.setDate(s.getDate() - (datePreset.value === '30g' ? 30 : 90));
      return { start: isoDay(s), end: isoDay(today) };
    }
    default:
      return { start: '', end: '' };
  }
};
const rangeParams = () => {
  const p = new URLSearchParams();
  const { start, end } = computeRange();
  if (start) p.set('start', start);
  if (end) p.set('end', end);
  return p;
};

// Liste/export ortak sorgu parametreleri: tarih aralığı + kolon filtreleri.
const orderQueryParams = () => {
  const p = rangeParams();
  if (filterStatus.value) p.set('status', filterStatus.value);
  if (filterPayment.value) p.set('payment', filterPayment.value);
  if (filterOrderNo.value.trim()) p.set('q', filterOrderNo.value.trim());
  if (filterCustomer.value.trim())
    p.set('customer', filterCustomer.value.trim());
  return p;
};

const base = () => `store_finances/${props.channel}`;

const loadOverview = async () => {
  try {
    overview.value = await fetchMarketplaceJson(
      accountId,
      `${base()}/overview?${rangeParams().toString()}`
    );
  } catch (e) {
    overview.value = {};
  }
};
const loadExpenses = async () => {
  try {
    const r = await fetchMarketplaceJson(accountId, `${base()}/expenses`);
    expenses.value = r.expenses || [];
  } catch (e) {
    expenses.value = [];
  }
};
const loadOrders = async (page = 1) => {
  try {
    const p = orderQueryParams();
    p.set('page', page);
    const r = await fetchMarketplaceJson(
      accountId,
      `${base()}/orders?${p.toString()}`
    );
    orders.value = r.orders || [];
    summary.value = r.summary || {};
    returnsMeta.value = r.returns_meta || {};
    pagination.value = r.pagination || { page: 1, total_pages: 1 };
  } catch (e) {
    orders.value = [];
    summary.value = {};
  }
};

const refresh = async () => {
  loading.value = true;
  returnsLoaded.value = false; // kanal değişince iade listesi yeniden yüklensin
  returnedData.value = { summary: {}, rows: [] };
  await Promise.all([loadOverview(), loadExpenses(), loadOrders(1)]);
  if (orderTab.value === 'iadeler') loadReturnedCustomers();
  loading.value = false;
};
watch(() => props.channel, refresh);
watch([datePreset, customStart, customEnd], () => {
  loadOverview();
  loadOrders(1);
});
let searchTimer = null;
watch([filterStatus, filterPayment], () => loadOrders(1));
watch([filterOrderNo, filterCustomer], () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => loadOrders(1), 350);
});
onMounted(refresh);

const fmtDay = iso => (iso ? new Date(iso).toLocaleDateString('tr-TR') : '—');

// --- Excel / CSV indirme (Trendyol paneliyle aynı desen) ---
const exporting = ref(false);
const EXPORT_COLUMNS = [
  'Sipariş No',
  'Tarih',
  'Müşteri',
  'Kaynak',
  'Durum',
  'Adet',
  'Ciro',
  'Ürün Maliyeti',
  'Kargo Toplam',
  'Gidiş Kargo',
  'Kapıda Komisyon',
  'İade Kargo',
  'Kapıda Tahsilat',
  'Net Yatan',
  'Kesinti',
  'Ödeme',
  'Tahsilat',
  'Net Kâr',
];
const statusLabel = o =>
  CARGO_STATUS[o.effective_status] ? CARGO_STATUS[o.effective_status].l : '—';
const collectedLabel = o => {
  if (!o.is_cod) return 'Peşin';
  if (o.payment_collected === true) return 'Tahsil edildi';
  if (o.payment_collected === false) return 'Tahsil edilmedi';
  return 'Bilinmiyor';
};
const netLabel = o => {
  if (o.cancelled) return 'İptal';
  if (o.payment_missing) return 'Ödeme yok';
  if (o.net_profit == null) return 'maliyet eksik';
  return o.net_profit;
};
// Teslim olmuş ama kapıda tahsilat YOK (Sürat KapidanTahsilatTutari=0) = kredi kartı/peşin
// online satış → kapıda komisyon yerine "Kredi Kartı" gösterilir (kullanıcı isteği).
const isPrepaidSale = o =>
  o.cargo_freight && !o.cargo_undelivered && !o.cargo_cod_collected;
// Tüm kargo statüleri kırılımı (kargo durumu raporu) — adet azalan, etiket+renk CARGO_STATUS'tan.
const statusBreakdown = computed(() =>
  Object.entries(summary.value.status_breakdown || {})
    .filter(([, c]) => c > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([code, count]) => ({
      code,
      count,
      label: CARGO_STATUS[code]?.l || code,
      cls: CARGO_STATUS[code]?.c || 'bg-n-slate-3 text-n-slate-11',
    }))
);
const exportRow = o => [
  o.order_number,
  fmtDay(o.ordered_at),
  o.customer || '',
  o.source || '',
  statusLabel(o),
  o.units || 0,
  o.revenue || 0,
  o.product_cost || 0,
  o.cargo_total ?? '',
  o.cargo_freight ?? '',
  o.cargo_cod_commission ?? '',
  o.cargo_return_freight ?? '',
  o.cargo_cod_collected ?? '',
  o.cargo_net_paid ?? '',
  o.cargo_deduction ?? '',
  o.payment_method || '',
  collectedLabel(o),
  netLabel(o),
];
const triggerDownload = (content, filename, mime) => {
  const blob = new Blob(['﻿', content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};
const fetchExportRows = async () => {
  const data = await fetchMarketplaceJson(
    accountId,
    `${base()}/export?${orderQueryParams().toString()}`
  );
  return data.orders || [];
};
const stamp = () => new Date().toISOString().slice(0, 10);
const exportCsv = async () => {
  if (exporting.value) return;
  exporting.value = true;
  try {
    const rows = await fetchExportRows();
    const head = EXPORT_COLUMNS.join(';');
    const lines = rows.map(o =>
      exportRow(o)
        .map(v => `"${String(v ?? '').replace(/"/g, '""')}"`)
        .join(';')
    );
    triggerDownload(
      [head, ...lines].join('\r\n'),
      `${props.channel}-finans-${stamp()}.csv`,
      'text/csv;charset=utf-8;'
    );
  } catch (e) {
    // sessiz
  }
  exporting.value = false;
};
const exportExcel = async () => {
  if (exporting.value) return;
  exporting.value = true;
  try {
    const rows = await fetchExportRows();
    const esc = s =>
      String(s ?? '').replace(
        /[&<>]/g,
        c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]
      );
    const head = EXPORT_COLUMNS.map(c => `<th>${esc(c)}</th>`).join('');
    const body = rows
      .map(
        o =>
          `<tr>${exportRow(o)
            .map(v => `<td>${esc(v)}</td>`)
            .join('')}</tr>`
      )
      .join('');
    const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
    triggerDownload(
      html,
      `${props.channel}-finans-${stamp()}.xls`,
      'application/vnd.ms-excel;charset=utf-8;'
    );
  } catch (e) {
    // sessiz
  }
  exporting.value = false;
};

// Gider ekleme formu
const today = new Date();
const yearStart = `${today.getFullYear()}-01-01`;
const yearEnd = `${today.getFullYear()}-12-31`;
const form = ref({
  category: 'altyapi',
  label: '',
  amount: '',
  period_start: yearStart,
  period_end: yearEnd,
});
const saving = ref(false);
const submitExpense = async () => {
  if (!form.value.label || !form.value.amount) return;
  saving.value = true;
  try {
    await postMarketplaceJsonBody(accountId, `${base()}/expenses`, {
      expense: { ...form.value },
    });
    form.value.label = '';
    form.value.amount = '';
    await Promise.all([loadExpenses(), loadOverview()]);
  } catch (e) {
    // sessiz
  }
  saving.value = false;
};
const removeExpense = async id => {
  try {
    await deleteMarketplaceJson(accountId, `${base()}/expenses/${id}`);
    await Promise.all([loadExpenses(), loadOverview()]);
  } catch (e) {
    // sessiz
  }
};

// Sipariş detay modalı
const selectedOrder = ref(null);
const orderDetail = ref({});
const orderProducts = ref([]);
const openOrder = async o => {
  selectedOrder.value = o;
  orderProducts.value = [];
  orderDetail.value = {};
  try {
    const r = await fetchMarketplaceJson(
      accountId,
      `${base()}/order?id=${o.id}`
    );
    orderDetail.value = r;
    orderProducts.value = r.products || [];
  } catch (e) {
    orderProducts.value = [];
  }
};
const closeOrder = () => {
  selectedOrder.value = null;
};

const moneyFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const money = v => `${moneyFmt.format(Number(v) || 0)} ₺`;
const title = computed(() => TITLES[props.channel] || props.channel);
// Kargo taşıyıcısı kanala göre: Ticimax/Koza → Aras, Shopify/ModaXLarge → Sürat.
const carrier = computed(() =>
  props.channel === 'shopify' ? 'Sürat' : 'Aras'
);
// Kargo gideri etiketi: Ticimax/Koza Nisan'da Aras'a geçti, öncesi Sürat → ikisi de toplamda.
const cargoCarrierLabel = computed(() =>
  props.channel === 'shopify' ? 'Sürat' : 'Aras + Sürat'
);
const profitNeg = computed(() => Number(overview.value.net_profit) < 0);
const periodLabel = computed(() => {
  const { start, end } = computeRange();
  if (!start && !end) return '1 Ocak 2026 → bugün';
  const f = d =>
    d
      ? new Date(d).toLocaleDateString('tr-TR', {
          day: '2-digit',
          month: 'short',
        })
      : '…';
  return `${f(start)} → ${f(end)}`;
});
const expenseByCat = computed(() => overview.value.expense_by_category || {});
const arasCargoTotal = computed(() => overview.value.aras_cargo?.total || 0);
// Kapıda ödeme mutabakatı (nakit akışı — GİDER DEĞİL): tahsil edilen / net yatan / bekleyen.
const cod = computed(() => overview.value.aras_cargo?.cod || {});
// COD mutabakatı yalnız Sürat veriyor (Aras KapidanTahsilatTutari vermiyor). Ticimax'ta
// sadece Nisan öncesi Koza'yı kapsar = eksik/yanıltıcı → kartı sadece Shopify'da göster.
const hasCod = computed(
  () => props.channel === 'shopify' && (cod.value.collected || 0) > 0
);
// İade eden müşteriler (Mac köprüsü musteri_iadeleri.json → endpoint). Tembel yüklenir.
const returnedData = ref({ summary: {}, rows: [] });
const returnsLoaded = ref(false);
const returnSearch = ref('');
const returnType = ref('all'); // all | post | undeliverable
const loadReturnedCustomers = async () => {
  try {
    returnedData.value = await fetchMarketplaceJson(
      accountId,
      `${base()}/returned_customers`
    );
  } catch (e) {
    returnedData.value = { summary: {}, rows: [] };
  }
  returnsLoaded.value = true;
};
const filteredReturns = computed(() => {
  let rows = returnedData.value.rows || [];
  if (returnType.value === 'post')
    rows = rows.filter(r => r.Tur === 'Teslim Sonrası Müşteri İadesi');
  else if (returnType.value === 'undeliverable')
    rows = rows.filter(r => r.Tur !== 'Teslim Sonrası Müşteri İadesi');
  const q = returnSearch.value.toLowerCase().trim();
  if (q) rows = rows.filter(r => JSON.stringify(r).toLowerCase().includes(q));
  return rows;
});
// Sipariş Bazlı Finans içindeki sekme: siparisler | iadeler (ayrı katman değil).
const orderTab = ref('siparisler');
const setOrderTab = t => {
  orderTab.value = t;
  if (t === 'iadeler' && !returnsLoaded.value) loadReturnedCustomers();
};
const toggle = s => {
  openSection.value = openSection.value === s ? null : s;
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="flex flex-col gap-4 p-6 mx-auto w-full max-w-[1500px]">
      <!-- Başlık -->
      <div class="flex items-center gap-2">
        <span class="i-lucide-store text-2xl text-n-slate-12" />
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ title }} Finans
        </h1>
      </div>

      <!-- Üst özet: Gerçek Kâr/Zarar -->
      <div
        class="flex flex-col gap-3 p-5 rounded-2xl bg-n-solid-2 border border-n-weak"
      >
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span
            class="text-xs font-medium tracking-wide text-n-slate-10 uppercase"
            >Gerçek Kâr/Zarar · {{ periodLabel }}</span
          >
          <div
            class="flex items-center p-0.5 rounded-lg bg-n-alpha-black1 gap-0.5"
          >
            <button
              v-for="opt in DATE_PRESETS"
              :key="opt.v"
              class="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
              :class="
                datePreset === opt.v
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="datePreset = opt.v"
            >
              {{ opt.l }}
            </button>
          </div>
        </div>
        <div
          v-if="datePreset === 'custom'"
          class="flex flex-wrap items-center gap-2 -mt-1"
        >
          <input
            v-model="customStart"
            type="date"
            class="px-2 py-1 text-xs rounded-md bg-n-solid-1 border border-n-weak text-n-slate-12"
          />
          <span class="text-n-slate-10 text-xs">→</span>
          <input
            v-model="customEnd"
            type="date"
            class="px-2 py-1 text-xs rounded-md bg-n-solid-1 border border-n-weak text-n-slate-12"
          />
        </div>

        <div class="grid grid-cols-2 md:grid-cols-4 2xl:grid-cols-7 gap-2">
          <div
            class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-solid-1 border border-n-weak"
          >
            <span class="text-xs text-n-slate-11">Ciro</span>
            <span class="text-lg font-bold tabular-nums text-n-slate-12">{{
              money(overview.revenue)
            }}</span>
            <span class="text-[11px] text-n-slate-10"
              >{{ overview.order_count || 0 }} sipariş</span
            >
          </div>
          <div
            class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-solid-1 border border-n-weak"
          >
            <span class="text-xs text-n-slate-11">− Ürün Maliyeti</span>
            <span
              class="text-lg font-bold tabular-nums text-ruby-700 dark:text-ruby-400"
              >{{ money(overview.product_cost) }}</span
            >
            <span class="text-[11px] text-n-slate-10"
              >kapsama %{{ overview.cost_coverage || 0 }}</span
            >
          </div>
          <div
            v-if="overview.zeroed_sales > 0"
            class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-solid-1 border border-n-weak"
          >
            <span class="text-xs text-n-slate-11">− Maliyetsiz Satış</span>
            <span
              class="text-lg font-bold tabular-nums text-ruby-700 dark:text-ruby-400"
              >{{ money(overview.zeroed_sales) }}</span
            >
            <span class="text-[11px] text-n-slate-10">kâr 0 sayıldı</span>
          </div>
          <div
            class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-solid-1 border border-n-weak"
          >
            <span class="text-xs text-n-slate-11">− Reklam (Meta)</span>
            <span
              class="text-lg font-bold tabular-nums text-ruby-700 dark:text-ruby-400"
              >{{ money(overview.ad_spend) }}</span
            >
            <span class="text-[11px] text-n-slate-10">otomatik</span>
          </div>
          <div
            v-if="arasCargoTotal > 0"
            class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-solid-1 border border-n-weak"
          >
            <span class="text-xs text-n-slate-11"
              >− Kargo ({{ cargoCarrierLabel }})</span
            >
            <span
              class="text-lg font-bold tabular-nums text-ruby-700 dark:text-ruby-400"
              >{{ money(arasCargoTotal) }}</span
            >
            <span class="text-[11px] text-n-slate-10">taşıma + kapıda kom.</span>
          </div>
          <div
            class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-solid-1 border border-n-weak"
          >
            <span class="text-xs text-n-slate-11">− Giderler</span>
            <span
              class="text-lg font-bold tabular-nums text-ruby-700 dark:text-ruby-400"
              >{{ money(overview.manual_expenses) }}</span
            >
            <span class="text-[11px] text-n-slate-10"
              >altyapı/diğer (manuel)</span
            >
          </div>
          <div
            class="flex flex-col gap-0.5 p-3 rounded-xl ring-2 col-span-2 md:col-span-4 2xl:col-span-1"
            :class="
              profitNeg
                ? 'bg-ruby-50 dark:bg-ruby-900/20 ring-ruby-400'
                : 'bg-green-50 dark:bg-green-900/20 ring-green-400'
            "
          >
            <span
              class="text-xs font-semibold"
              :class="
                profitNeg
                  ? 'text-ruby-800 dark:text-ruby-300'
                  : 'text-green-800 dark:text-green-300'
              "
              >= GERÇEK {{ profitNeg ? 'ZARAR' : 'KÂR' }}</span
            >
            <span
              class="text-2xl font-bold tabular-nums"
              :class="
                profitNeg
                  ? 'text-ruby-700 dark:text-ruby-400'
                  : 'text-green-700 dark:text-green-400'
              "
              >{{ money(overview.net_profit) }}</span
            >
            <span class="text-[11px] text-n-slate-10"
              >ciro − maliyet − gider</span
            >
          </div>
        </div>

        <p
          v-if="(overview.cost_coverage || 0) < 100"
          class="text-[11px] text-yellow-700 dark:text-yellow-400 p-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/10 max-w-3xl"
        >
          ⚠️ Siparişlerin %{{ overview.cost_coverage || 0 }}'inin ürün maliyeti
          biliniyor; maliyeti bilinmeyenler "maliyetine satıldı" (kâr 0) sayıldı
          (temkinli). Bu modeller eklendikçe gerçek kâr nettleşir.
        </p>
      </div>

      <!-- Kapıda Ödeme Mutabakatı (nakit akışı, GİDER DEĞİL — çift saymaz) -->
      <div
        v-if="hasCod"
        class="flex flex-col gap-3 p-5 rounded-2xl bg-n-solid-2 border border-n-weak"
      >
        <div class="flex items-center gap-2">
          <span class="i-lucide-banknote text-lg text-n-slate-11" />
          <span
            class="text-xs font-medium tracking-wide text-n-slate-10 uppercase"
            >Kapıda Ödeme Mutabakatı · {{ carrier }} · {{ periodLabel }}</span
          >
        </div>
        <div class="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-11">Kapıda Tahsil Edilen</span>
            <span class="text-xl font-semibold tabular-nums text-n-slate-12">{{
              money(cod.collected)
            }}</span>
            <span class="text-[11px] text-n-slate-10"
              >{{ cod.cod_orders || 0 }} kapıda ödemeli sipariş</span
            >
          </div>
          <span class="text-2xl font-light text-n-slate-9">→</span>
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-11">Sana Net Yatan</span>
            <span
              class="text-xl font-semibold tabular-nums text-green-700 dark:text-green-400"
              >{{ money(cod.net_paid) }}</span
            >
            <span class="text-[11px] text-n-slate-10"
              >{{ cod.settled_orders || 0 }} mutabık sipariş</span
            >
          </div>
          <span class="text-2xl font-light text-n-slate-9">·</span>
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-11"
              >Bekleyen ({{ carrier }}'ta)</span
            >
            <span
              class="text-xl font-semibold tabular-nums text-yellow-700 dark:text-yellow-400"
              >{{ money(cod.pending) }}</span
            >
            <span class="text-[11px] text-n-slate-10"
              >tahsil edildi, henüz yatmadı</span
            >
          </div>
        </div>
        <p class="text-[11px] text-n-slate-10 max-w-3xl">
          Kapıda tahsil edilen müşteriden alınan paradır; {{ carrier }} navlunu
          kesip kalanını sana yatırır (tahsilat = net yatan + kargo). Bu blok
          nakit akışı takibidir, gidere/kâra EKLENMEZ (çift sayma olmaz).
        </p>
      </div>

      <!-- Sipariş Bazlı Finans (en üst katman) -->
      <div class="rounded-2xl border border-n-weak overflow-hidden">
        <button
          class="flex items-center justify-between w-full px-5 py-3 bg-n-solid-2 hover:bg-n-alpha-black1"
          @click="toggle('orders')"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-list-ordered text-lg text-n-slate-11" />
            <span class="text-base font-semibold text-n-slate-12"
              >Sipariş Bazlı Finans</span
            >
            <span class="text-xs text-n-slate-10"
              >tüm siparişler: ciro, ürün maliyeti, kargo (gidiş + kapıda
              komisyon + iade), canlı kargo durumu, ödeme/tahsilat ve net
              kâr</span
            >
          </div>
          <span
            class="i-lucide-chevron-down text-xl text-n-slate-11 transition-transform"
            :class="openSection === 'orders' ? 'rotate-180' : ''"
          />
        </button>
        <div
          v-if="openSection === 'orders'"
          class="flex flex-col gap-3 px-5 py-4 border-t border-n-weak"
        >
          <!-- Sekmeler: Siparişler / İade Edenler (ikisi de bu katmanın içinde) -->
          <div class="flex items-center gap-1 border-b border-n-weak -mt-1">
            <button
              class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
              :class="
                orderTab === 'siparisler'
                  ? 'border-woot-500 text-n-slate-12'
                  : 'border-transparent text-n-slate-10 hover:text-n-slate-11'
              "
              @click="orderTab = 'siparisler'"
            >
              Siparişler
            </button>
            <button
              class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
              :class="
                orderTab === 'iadeler'
                  ? 'border-woot-500 text-n-slate-12'
                  : 'border-transparent text-n-slate-10 hover:text-n-slate-11'
              "
              @click="setOrderTab('iadeler')"
            >
              İade Edenler
            </button>
          </div>

          <template v-if="orderTab === 'siparisler'">
            <!-- Operasyonel özet (ciro YOK — kâr/ciro üstteki katmanda) -->
            <p class="text-[11px] text-n-slate-10 -mb-1">
              {{ anyOrderFilter ? 'Filtreye göre:' : 'Tüm siparişler:' }}
            </p>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div
                class="flex flex-col p-3 rounded-xl bg-n-solid-2 border border-n-weak"
              >
                <span class="text-[11px] text-n-slate-10">Sipariş</span>
                <span class="text-lg font-bold tabular-nums text-n-slate-12">{{
                  summary.total_rows || 0
                }}</span>
              </div>
              <div
                class="flex flex-col p-3 rounded-xl bg-n-solid-2 border border-n-weak"
              >
                <span class="text-[11px] text-n-slate-10"
                  >Toplam Kargo Gideri</span
                >
                <span
                  class="text-lg font-bold tabular-nums text-ruby-700 dark:text-ruby-400"
                  >{{ money(summary.cargo_total) }}</span
                >
                <span class="text-[10px] text-n-slate-10"
                  >gidiş {{ money(summary.cargo_freight) }} · kom.
                  {{ money(summary.cargo_commission) }} · iade
                  {{ money(summary.cargo_return_freight) }}</span
                >
              </div>
            </div>

            <!-- İade verisi tazeliği: Sürat fatura/iade raporu geriden gelir; panel bunu
                 saklamayıp dürüstçe söyler (yoksa "iade tutarları neden yok" karışıklığı doğuyor) -->
            <p
              v-if="returnsMeta.last_return_order_on"
              class="text-[11px] text-yellow-700 dark:text-yellow-400 px-2.5 py-1.5 rounded-lg bg-yellow-50 dark:bg-yellow-900/10 max-w-3xl"
            >
              ℹ️ İade / dönüş kargo tutarlarının fatura kanıtı {{ carrier }}'dan
              2-4 hafta geriden gelir. Fiilen geri dönen kargolar beklemeden "ön
              kayıt" olarak işlenir (dönüş navlunu varsayılan ~51,63 ₺; statüde
              "ön kayıt" yazar) ve fatura gelince kesin tutarlarla güncellenir.
              Son fatura-kanıtlı iade:
              <b>{{ returnsMeta.last_return_order_on }}</b>.
            </p>

            <!-- Tüm kargo durumu raporu (her statü + adet) -->
            <div v-if="statusBreakdown.length" class="flex flex-col gap-1.5">
              <span class="text-[11px] text-n-slate-10"
                >Kargo durumu dağılımı:</span
              >
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="s in statusBreakdown"
                  :key="s.code"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                  :class="s.cls"
                >
                  {{ s.label }}
                  <b class="tabular-nums">{{ s.count }}</b>
                </span>
              </div>
            </div>

            <!-- Araç çubuğu: filtre özeti + Excel/CSV indir -->
            <div class="flex flex-wrap items-center justify-between gap-2">
              <button
                v-if="anyOrderFilter"
                class="px-2.5 py-1.5 text-xs rounded-lg text-n-slate-11 hover:bg-n-alpha-black1 flex items-center gap-1"
                @click="clearOrderFilters"
              >
                <span class="i-lucide-filter-x" /> Filtreleri temizle
              </button>
              <span v-else class="text-xs text-n-slate-10">
                Kolon başlıklarındaki
                <span class="i-lucide-filter align-middle" /> ikonuna tıklayarak
                filtreleyebilirsin.
              </span>
              <div class="flex items-center gap-2">
                <button
                  class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white disabled:opacity-50"
                  :disabled="exporting"
                  @click="exportExcel"
                >
                  <span class="i-lucide-file-spreadsheet" />
                  {{ exporting ? 'Hazırlanıyor…' : 'Excel indir' }}
                </button>
                <button
                  class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-n-solid-2 border border-n-weak text-n-slate-11 disabled:opacity-50"
                  :disabled="exporting"
                  @click="exportCsv"
                >
                  <span class="i-lucide-download" /> CSV indir
                </button>
              </div>
            </div>

            <div class="relative rounded-xl border border-n-weak">
              <!-- Açık filtre menüsünü kapatmak için saydam arka plan -->
              <div
                v-if="activeHeaderFilter"
                class="fixed inset-0 z-40"
                @click="activeHeaderFilter = null"
              />
              <table class="w-full text-sm border-collapse min-w-[900px]">
                <thead>
                  <tr class="bg-n-solid-2 text-n-slate-11">
                    <th class="px-3 py-2 font-medium text-left relative">
                      <span class="inline-flex items-center gap-1">
                        Sipariş
                        <button
                          class="text-sm"
                          :class="
                            filterOrderNo
                              ? 'i-lucide-filter text-woot-500'
                              : 'i-lucide-filter text-n-slate-9 hover:text-n-slate-11'
                          "
                          @click.stop="toggleHeaderFilter('order')"
                        />
                      </span>
                      <div
                        v-if="activeHeaderFilter === 'order'"
                        class="absolute z-50 left-2 top-full mt-1 p-2 rounded-lg bg-n-background border border-n-weak shadow-lg w-56"
                        @click.stop
                      >
                        <input
                          v-model="filterOrderNo"
                          type="text"
                          placeholder="Sipariş no…"
                          class="w-full px-2 py-1.5 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                        />
                      </div>
                    </th>
                    <th class="px-3 py-2 font-medium text-left relative">
                      <span class="inline-flex items-center gap-1">
                        Tarih
                        <button
                          class="text-sm"
                          :class="
                            datePreset !== 'all'
                              ? 'i-lucide-filter text-woot-500'
                              : 'i-lucide-filter text-n-slate-9 hover:text-n-slate-11'
                          "
                          @click.stop="toggleHeaderFilter('date')"
                        />
                      </span>
                      <div
                        v-if="activeHeaderFilter === 'date'"
                        class="absolute z-50 left-2 top-full mt-1 p-3 rounded-lg bg-n-background border border-n-weak shadow-lg w-64 flex flex-col gap-2"
                        @click.stop
                      >
                        <div class="flex items-center gap-1.5">
                          <input
                            v-model="customStart"
                            type="date"
                            class="flex-1 px-2 py-1 text-xs rounded-md bg-n-solid-1 border border-n-weak text-n-slate-12"
                            @change="datePreset = 'custom'"
                          />
                          <span class="text-n-slate-10 text-xs">→</span>
                          <input
                            v-model="customEnd"
                            type="date"
                            class="flex-1 px-2 py-1 text-xs rounded-md bg-n-solid-1 border border-n-weak text-n-slate-12"
                            @change="datePreset = 'custom'"
                          />
                        </div>
                        <div class="flex flex-wrap gap-1">
                          <button
                            v-for="opt in DATE_PRESETS"
                            :key="opt.v"
                            class="px-2 py-0.5 text-[11px] rounded-md"
                            :class="
                              datePreset === opt.v
                                ? 'bg-woot-500 text-white'
                                : 'bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-black1'
                            "
                            @click="datePreset = opt.v"
                          >
                            {{ opt.l }}
                          </button>
                        </div>
                      </div>
                    </th>
                    <th class="px-3 py-2 font-medium text-left relative">
                      <span class="inline-flex items-center gap-1">
                        Müşteri
                        <button
                          class="text-sm"
                          :class="
                            filterCustomer
                              ? 'i-lucide-filter text-woot-500'
                              : 'i-lucide-filter text-n-slate-9 hover:text-n-slate-11'
                          "
                          @click.stop="toggleHeaderFilter('customer')"
                        />
                      </span>
                      <div
                        v-if="activeHeaderFilter === 'customer'"
                        class="absolute z-50 left-2 top-full mt-1 p-2 rounded-lg bg-n-background border border-n-weak shadow-lg w-56"
                        @click.stop
                      >
                        <input
                          v-model="filterCustomer"
                          type="text"
                          placeholder="Müşteri adı…"
                          class="w-full px-2 py-1.5 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                        />
                      </div>
                    </th>
                    <th class="px-3 py-2 font-medium text-left">Kargo</th>
                    <th class="px-3 py-2 font-medium text-right">
                      Gidiş Kargo
                    </th>
                    <th class="px-3 py-2 font-medium text-right">
                      Kapıda Kom.
                    </th>
                    <th class="px-3 py-2 font-medium text-right">İade Kargo</th>
                    <th class="px-3 py-2 font-medium text-left relative">
                      <span class="inline-flex items-center gap-1">
                        Kargo Durumu
                        <button
                          class="text-sm"
                          :class="
                            filterStatus
                              ? 'i-lucide-filter text-woot-500'
                              : 'i-lucide-filter text-n-slate-9 hover:text-n-slate-11'
                          "
                          @click.stop="toggleHeaderFilter('status')"
                        />
                      </span>
                      <div
                        v-if="activeHeaderFilter === 'status'"
                        class="absolute z-50 left-2 top-full mt-1 p-1 rounded-lg bg-n-background border border-n-weak shadow-lg w-48 max-h-72 overflow-auto"
                        @click.stop
                      >
                        <button
                          class="block w-full text-left px-2 py-1.5 text-xs rounded-md hover:bg-n-alpha-black1"
                          :class="
                            !filterStatus
                              ? 'text-woot-500 font-semibold'
                              : 'text-n-slate-11'
                          "
                          @click="
                            filterStatus = '';
                            activeHeaderFilter = null;
                          "
                        >
                          Tümü
                        </button>
                        <button
                          v-for="s in STATUS_FILTERS"
                          :key="s"
                          class="block w-full text-left px-2 py-1.5 text-xs rounded-md hover:bg-n-alpha-black1"
                          :class="
                            filterStatus === s
                              ? 'text-woot-500 font-semibold'
                              : 'text-n-slate-11'
                          "
                          @click="
                            filterStatus = s;
                            activeHeaderFilter = null;
                          "
                        >
                          {{ cargoStatus(s) ? cargoStatus(s).l : s }}
                        </button>
                      </div>
                    </th>
                    <th class="px-3 py-2 font-medium text-left relative">
                      <span class="inline-flex items-center gap-1">
                        Ödeme / Tahsilat
                        <button
                          class="text-sm"
                          :class="
                            filterPayment
                              ? 'i-lucide-filter text-woot-500'
                              : 'i-lucide-filter text-n-slate-9 hover:text-n-slate-11'
                          "
                          @click.stop="toggleHeaderFilter('payment')"
                        />
                      </span>
                      <div
                        v-if="activeHeaderFilter === 'payment'"
                        class="absolute z-50 right-2 top-full mt-1 p-1 rounded-lg bg-n-background border border-n-weak shadow-lg w-48"
                        @click.stop
                      >
                        <button
                          v-for="opt in [
                            { v: '', l: 'Tümü' },
                            { v: 'Nakit', l: 'Kapıda Nakit' },
                            { v: 'Kredi', l: 'Kapıda Kredi Kartı' },
                            { v: 'Havale', l: 'Havale (peşin)' },
                          ]"
                          :key="opt.v"
                          class="block w-full text-left px-2 py-1.5 text-xs rounded-md hover:bg-n-alpha-black1"
                          :class="
                            filterPayment === opt.v
                              ? 'text-woot-500 font-semibold'
                              : 'text-n-slate-11'
                          "
                          @click="
                            filterPayment = opt.v;
                            activeHeaderFilter = null;
                          "
                        >
                          {{ opt.l }}
                        </button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="o in orders"
                    :key="o.id"
                    class="border-t border-n-weak cursor-pointer hover:bg-n-alpha-black1"
                    @click="openOrder(o)"
                  >
                    <td
                      class="px-3 py-2 font-medium text-woot-600 dark:text-woot-400"
                    >
                      {{ o.order_number }}
                    </td>
                    <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                      {{ fmtDay(o.ordered_at) }}
                    </td>
                    <td class="px-3 py-2 text-n-slate-11">
                      {{ o.customer || '—' }}
                    </td>
                    <td class="px-3 py-2">
                      <span
                        v-if="o.carrier === 'aras'"
                        class="inline-block px-2 py-0.5 rounded text-[10px] font-bold tracking-wide bg-woot-900 text-white"
                        >ARAS</span
                      >
                      <span
                        v-else
                        class="inline-block px-2 py-0.5 rounded text-[10px] font-bold tracking-wide bg-red-600 text-white"
                        >SÜRAT</span
                      >
                    </td>
                    <td class="px-3 py-2 text-right tabular-nums">
                      <span
                        v-if="o.cargo_freight"
                        class="text-ruby-700 dark:text-ruby-400"
                        >{{ money(-o.cargo_freight) }}</span
                      >
                      <span v-else class="text-n-slate-10 text-xs">—</span>
                    </td>
                    <td class="px-3 py-2 text-right tabular-nums">
                      <span
                        v-if="o.cargo_cod_commission"
                        class="text-ruby-700 dark:text-ruby-400"
                        >{{ money(-o.cargo_cod_commission) }}</span
                      >
                      <span
                        v-else-if="isPrepaidSale(o)"
                        class="text-green-700 dark:text-green-400 text-xs font-medium"
                        >Kredi Kartı</span
                      >
                      <span v-else class="text-n-slate-10 text-xs">—</span>
                    </td>
                    <td class="px-3 py-2 text-right tabular-nums">
                      <span
                        v-if="o.cargo_return_freight"
                        class="text-ruby-700 dark:text-ruby-400"
                        >{{ money(-o.cargo_return_freight) }}</span
                      >
                      <span v-else class="text-n-slate-10 text-xs">—</span>
                    </td>
                    <td class="px-3 py-2 text-left">
                      <span
                        v-if="cargoStatus(o.effective_status)"
                        class="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        :class="cargoStatus(o.effective_status).c"
                        >{{ cargoStatus(o.effective_status).l }}</span
                      >
                      <span v-else class="text-n-slate-10 text-xs">—</span>
                    </td>
                    <td class="px-3 py-2 text-left whitespace-nowrap">
                      <span class="text-n-slate-11">{{
                        o.payment_method
                      }}</span>
                      <span
                        v-if="o.is_cod && o.payment_collected === true"
                        class="block text-[10px] font-semibold text-green-700 dark:text-green-400"
                        >Tahsil edildi</span
                      >
                      <span
                        v-else-if="o.is_cod && o.payment_collected === false"
                        class="block text-[10px] font-semibold text-n-slate-10"
                        >Tahsil edilmedi</span
                      >
                      <span
                        v-else-if="o.is_cod"
                        class="block text-[10px] text-n-slate-10"
                        >Tahsilat bilinmiyor</span
                      >
                      <span v-else class="block text-[10px] text-n-slate-10">
                        Peşin
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              v-if="pagination.total_pages > 1"
              class="flex items-center justify-center gap-2"
            >
              <button
                class="px-3 py-1.5 text-sm rounded-lg bg-n-solid-2 border border-n-weak disabled:opacity-40"
                :disabled="pagination.page <= 1"
                @click="loadOrders(pagination.page - 1)"
              >
                Önceki
              </button>
              <span class="text-sm text-n-slate-11"
                >Sayfa {{ pagination.page }} /
                {{ pagination.total_pages }}</span
              >
              <button
                class="px-3 py-1.5 text-sm rounded-lg bg-n-solid-2 border border-n-weak disabled:opacity-40"
                :disabled="pagination.page >= pagination.total_pages"
                @click="loadOrders(pagination.page + 1)"
              >
                Sonraki
              </button>
            </div>
          </template>

          <template v-else>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div
                class="flex flex-col p-3 rounded-xl bg-ruby-50 dark:bg-ruby-900/15 border border-ruby-200 dark:border-ruby-900"
              >
                <span class="text-[11px] text-ruby-800 dark:text-ruby-300"
                  >Teslim Sonrası İade</span
                >
                <span
                  class="text-lg font-bold tabular-nums text-ruby-700 dark:text-ruby-400"
                  >{{ returnedData.summary.post_delivery || 0 }}</span
                >
                <span class="text-[10px] text-n-slate-10"
                  >müşteri aldı → geri gönderdi</span
                >
              </div>
              <div
                class="flex flex-col p-3 rounded-xl bg-yellow-50 dark:bg-yellow-900/15 border border-yellow-200 dark:border-yellow-900"
              >
                <span class="text-[11px] text-yellow-800 dark:text-yellow-300"
                  >Teslim Edilemeyen</span
                >
                <span
                  class="text-lg font-bold tabular-nums text-yellow-700 dark:text-yellow-400"
                  >{{ returnedData.summary.undeliverable || 0 }}</span
                >
                <span class="text-[10px] text-n-slate-10"
                  >adres/kabul sorunu</span
                >
              </div>
              <div
                class="flex flex-col p-3 rounded-xl bg-n-solid-2 border border-n-weak"
              >
                <span class="text-[11px] text-n-slate-10"
                  >Toplam İade Eden</span
                >
                <span class="text-lg font-bold tabular-nums text-n-slate-12">{{
                  returnedData.summary.total || 0
                }}</span>
              </div>
              <div
                class="flex flex-col p-3 rounded-xl bg-n-solid-2 border border-n-weak"
              >
                <span class="text-[11px] text-n-slate-10"
                  >İade Kargo Bedeli</span
                >
                <span
                  class="text-lg font-bold tabular-nums text-ruby-700 dark:text-ruby-400"
                  >{{ money(returnedData.summary.return_freight) }}</span
                >
              </div>
            </div>

            <div class="flex flex-wrap items-center gap-2">
              <button
                v-for="t in [
                  { v: 'all', l: 'Hepsi' },
                  { v: 'post', l: '↩️ Teslim Sonrası İade' },
                  { v: 'undeliverable', l: '📍 Teslim Edilemeyen' },
                ]"
                :key="t.v"
                class="px-3 py-1 text-xs font-medium rounded-lg border"
                :class="
                  returnType === t.v
                    ? 'bg-n-solid-1 text-n-slate-12 border-n-weak'
                    : 'bg-n-alpha-black1 text-n-slate-10 border-transparent'
                "
                @click="returnType = t.v"
              >
                {{ t.l }}
              </button>
              <input
                v-model="returnSearch"
                type="text"
                placeholder="Ara: müşteri, adres, il, sipariş…"
                class="ml-auto px-3 py-1.5 text-sm rounded-lg bg-n-solid-2 border border-n-weak w-72 max-w-full"
              />
            </div>

            <div
              class="overflow-auto max-h-[600px] rounded-xl border border-n-weak"
            >
              <table class="w-full text-sm">
                <thead
                  class="sticky top-0 bg-n-solid-2 text-n-slate-10 text-xs"
                >
                  <tr>
                    <th class="px-3 py-2 text-left font-medium">Müşteri</th>
                    <th class="px-3 py-2 text-left font-medium">Tür</th>
                    <th class="px-3 py-2 text-left font-medium">Adres</th>
                    <th class="px-3 py-2 text-left font-medium">Geri Geliş</th>
                    <th class="px-3 py-2 text-right font-medium">İade Kargo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(r, i) in filteredReturns"
                    :key="i"
                    class="border-t border-n-weak"
                  >
                    <td class="px-3 py-2 font-medium text-n-slate-12">
                      {{ r.MusteriAdi || '—' }}
                    </td>
                    <td class="px-3 py-2">
                      <span
                        class="px-2 py-0.5 rounded-full text-xs font-medium"
                        :class="
                          r.Tur === 'Teslim Sonrası Müşteri İadesi'
                            ? 'bg-ruby-100 text-ruby-800 dark:bg-ruby-900 dark:text-ruby-300'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                        "
                        >{{
                          r.Tur === 'Teslim Sonrası Müşteri İadesi'
                            ? 'Teslim Sonrası'
                            : 'Edilemeyen'
                        }}</span
                      >
                      <span
                        v-if="r.IadeSebebi"
                        class="block text-[10px] text-n-slate-10 mt-0.5"
                        >{{ r.IadeSebebi }}</span
                      >
                    </td>
                    <td class="px-3 py-2 text-n-slate-11 max-w-[320px] text-xs">
                      {{ r.Adres || '—' }}
                    </td>
                    <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                      {{ r.GeriGelisTarihi || '—' }}
                    </td>
                    <td
                      class="px-3 py-2 text-right tabular-nums text-ruby-700 dark:text-ruby-400"
                    >
                      {{ money(r.IadeKargoBedeli) }}
                    </td>
                  </tr>
                  <tr v-if="!filteredReturns.length">
                    <td
                      colspan="5"
                      class="px-3 py-6 text-center text-n-slate-10"
                    >
                      Kayıt yok.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>

      <!-- Gider Yönetimi -->
      <div class="rounded-2xl border border-n-weak overflow-hidden">
        <button
          class="flex items-center justify-between w-full px-5 py-3 bg-n-solid-2 hover:bg-n-alpha-black1"
          @click="toggle('expenses')"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-receipt-text text-lg text-n-slate-11" />
            <span class="text-base font-semibold text-n-slate-12"
              >Gider Yönetimi</span
            >
            <span class="text-xs text-n-slate-10"
              >yıllık altyapı · reklam · kargo (manuel)</span
            >
          </div>
          <span
            class="i-lucide-chevron-down text-xl text-n-slate-11 transition-transform"
            :class="openSection === 'expenses' ? 'rotate-180' : ''"
          />
        </button>
        <div
          v-if="openSection === 'expenses'"
          class="flex flex-col gap-4 px-5 py-4 border-t border-n-weak"
        >
          <!-- Kategori özetleri -->
          <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div
              v-for="cat in BREAKDOWN_CATEGORIES"
              :key="cat.v"
              class="flex flex-col p-3 rounded-xl bg-n-solid-2 border border-n-weak"
            >
              <span class="text-[11px] text-n-slate-10">{{ cat.l }}</span>
              <span
                class="text-base font-semibold tabular-nums text-n-slate-12"
                >{{ money(expenseByCat[cat.v] || 0) }}</span
              >
            </div>
          </div>

          <!-- Ekleme formu -->
          <div
            class="flex flex-col gap-2 p-3 rounded-xl bg-n-solid-2 border border-n-weak"
          >
            <span class="text-sm font-medium text-n-slate-12"
              >Yeni Gider Ekle</span
            >
            <div class="flex flex-wrap items-end gap-2">
              <label class="flex flex-col gap-1">
                <span class="text-[11px] text-n-slate-10">Kategori</span>
                <select
                  v-model="form.category"
                  class="px-2 py-1.5 text-sm rounded-lg bg-n-solid-1 border border-n-weak text-n-slate-12"
                >
                  <option v-for="c in CATEGORIES" :key="c.v" :value="c.v">
                    {{ c.l }}
                  </option>
                </select>
              </label>
              <label class="flex flex-col gap-1 flex-1 min-w-[160px]">
                <span class="text-[11px] text-n-slate-10">Açıklama</span>
                <input
                  v-model="form.label"
                  placeholder="örn. Ticimax yıllık lisans"
                  class="px-2 py-1.5 text-sm rounded-lg bg-n-solid-1 border border-n-weak text-n-slate-12"
                />
              </label>
              <label class="flex flex-col gap-1">
                <span class="text-[11px] text-n-slate-10">Tutar (₺)</span>
                <input
                  v-model="form.amount"
                  type="number"
                  placeholder="0"
                  class="px-2 py-1.5 w-28 text-sm rounded-lg bg-n-solid-1 border border-n-weak text-n-slate-12 tabular-nums"
                />
              </label>
              <label class="flex flex-col gap-1">
                <span class="text-[11px] text-n-slate-10">Dönem Başı</span>
                <input
                  v-model="form.period_start"
                  type="date"
                  class="px-2 py-1.5 text-sm rounded-lg bg-n-solid-1 border border-n-weak text-n-slate-12"
                />
              </label>
              <label class="flex flex-col gap-1">
                <span class="text-[11px] text-n-slate-10">Dönem Sonu</span>
                <input
                  v-model="form.period_end"
                  type="date"
                  class="px-2 py-1.5 text-sm rounded-lg bg-n-solid-1 border border-n-weak text-n-slate-12"
                />
              </label>
              <button
                class="px-4 py-1.5 text-sm font-medium rounded-lg bg-woot-500 text-white disabled:opacity-40"
                :disabled="saving || !form.label || !form.amount"
                @click="submitExpense"
              >
                {{ saving ? 'Ekleniyor…' : 'Ekle' }}
              </button>
            </div>
            <p class="text-[11px] text-n-slate-10">
              Yıllık giderler (örn. altyapı) seçtiğin döneme gün-orantılı
              yansır. Kargo verisi şu an otomatik çekilemiyor — manuel
              girebilirsin.
            </p>
          </div>

          <!-- Gider listesi -->
          <div
            v-if="expenses.length"
            class="overflow-x-auto rounded-xl border border-n-weak"
          >
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-n-solid-2 text-n-slate-11">
                  <th class="px-3 py-2 font-medium text-left">Kategori</th>
                  <th class="px-3 py-2 font-medium text-left">Açıklama</th>
                  <th class="px-3 py-2 font-medium text-right">Tutar</th>
                  <th class="px-3 py-2 font-medium text-left">Dönem</th>
                  <th class="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="e in expenses"
                  :key="e.id"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2">
                    <span
                      class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-black1 text-n-slate-11"
                      >{{ e.category_label }}</span
                    >
                  </td>
                  <td class="px-3 py-2 text-n-slate-12">{{ e.label }}</td>
                  <td
                    class="px-3 py-2 text-right tabular-nums font-medium text-n-slate-12"
                  >
                    {{ money(e.amount) }}
                  </td>
                  <td
                    class="px-3 py-2 text-n-slate-10 text-xs whitespace-nowrap"
                  >
                    {{ fmtDay(e.period_start) }} – {{ fmtDay(e.period_end) }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    <button
                      class="text-ruby-600 hover:text-ruby-700 text-xs"
                      @click="removeExpense(e.id)"
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-sm text-n-slate-10">
            Henüz gider eklenmedi. Sabit giderlerini (yıllık altyapı, reklam,
            kargo) ekleyince gerçek kâr/zarar ortaya çıkar.
          </p>
        </div>
      </div>
    </div>

    <!-- Sipariş detay modalı -->
    <div
      v-if="selectedOrder"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      @click.self="closeOrder"
    >
      <div
        class="flex flex-col gap-3 p-5 w-full max-w-lg max-h-[85vh] overflow-auto rounded-2xl bg-n-background border border-n-weak"
      >
        <div class="flex items-center justify-between">
          <div class="flex flex-col">
            <span class="text-base font-semibold text-n-slate-12"
              >Sipariş {{ selectedOrder.order_number }}</span
            >
            <span class="text-xs text-n-slate-10"
              >{{ fmtDay(selectedOrder.ordered_at) }} ·
              {{ selectedOrder.customer || '—' }} ·
              {{ selectedOrder.source || '—' }}</span
            >
          </div>
          <button
            class="i-lucide-x text-xl text-n-slate-11 hover:text-n-slate-12"
            @click="closeOrder"
          />
        </div>

        <!-- Ürün kartları -->
        <div class="flex flex-col gap-2">
          <div
            v-for="(p, i) in orderProducts"
            :key="i"
            class="flex items-center gap-3 p-2 rounded-lg bg-n-solid-2 border border-n-weak"
          >
            <img
              v-if="p.image"
              :src="p.image"
              class="w-12 h-12 rounded-md object-cover bg-n-alpha-black2"
            />
            <div
              v-else
              class="flex items-center justify-center w-12 h-12 rounded-md bg-n-alpha-black2 text-n-slate-10"
            >
              <span class="i-lucide-shirt" />
            </div>
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm truncate text-n-slate-12">{{
                p.name || p.sku
              }}</span>
              <span class="text-xs text-n-slate-10">{{ p.sku }}</span>
            </div>
            <div class="text-right">
              <div class="text-sm text-n-slate-12 tabular-nums">
                {{ p.quantity }} ad · {{ money(p.price) }}
              </div>
              <div
                v-if="p.line_cost != null"
                class="text-xs text-n-slate-10 tabular-nums"
              >
                maliyet {{ money(p.line_cost) }}
              </div>
              <div v-else class="text-xs text-yellow-600 dark:text-yellow-400">
                maliyet girilmemiş
              </div>
            </div>
          </div>
        </div>

        <!-- Özet -->
        <div class="flex flex-col gap-1.5 pt-2 border-t border-n-weak text-sm">
          <div class="flex justify-between">
            <span class="text-n-slate-11">Ciro</span>
            <span class="font-medium tabular-nums text-n-slate-12">{{
              money(selectedOrder.revenue)
            }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-n-slate-11">Ürün Maliyeti</span>
            <span class="tabular-nums text-ruby-700 dark:text-ruby-400">{{
              money(-selectedOrder.product_cost)
            }}</span>
          </div>
          <template v-if="selectedOrder.cargo_total != null">
            <div class="flex justify-between">
              <span class="text-n-slate-11">Kargo Taşıma</span>
              <span class="tabular-nums text-ruby-700 dark:text-ruby-400">{{
                money(-selectedOrder.cargo_freight)
              }}</span>
            </div>
            <div
              v-if="selectedOrder.cargo_cod_commission > 0"
              class="flex justify-between"
            >
              <span class="text-n-slate-11">Kapıda Ödeme Komisyonu</span>
              <span class="tabular-nums text-ruby-700 dark:text-ruby-400">{{
                money(-selectedOrder.cargo_cod_commission)
              }}</span>
            </div>
            <div
              v-if="selectedOrder.cargo_return_freight > 0"
              class="flex justify-between"
            >
              <span class="text-n-slate-11"
                >İade (Dönüş) Kargosu — teslim edilemedi</span
              >
              <span class="tabular-nums text-ruby-700 dark:text-ruby-400">{{
                money(-selectedOrder.cargo_return_freight)
              }}</span>
            </div>
            <div
              v-if="selectedOrder.cargo_status"
              class="flex justify-between text-[11px]"
            >
              <span class="text-n-slate-10">Kargo Durumu</span>
              <span class="text-n-slate-11">{{
                selectedOrder.cargo_status
              }}</span>
            </div>
          </template>
          <div class="flex justify-between text-[11px]">
            <span class="text-n-slate-10">Ödeme / Tahsilat</span>
            <span class="text-n-slate-11">
              {{ selectedOrder.payment_method
              }}<template v-if="selectedOrder.is_cod">
                —
                {{
                  selectedOrder.payment_collected === true
                    ? 'tahsil edildi'
                    : selectedOrder.payment_collected === false
                      ? 'tahsil edilmedi'
                      : 'tahsilat bilinmiyor'
                }}</template
              ><template v-else> (peşin)</template>
            </span>
          </div>
          <div class="flex justify-between pt-1.5 border-t border-n-weak">
            <span class="text-base font-bold text-n-slate-12">Net Kâr</span>
            <span
              v-if="selectedOrder.cancelled"
              class="text-sm text-n-slate-10"
            >
              İptal — kâra dahil değil
            </span>
            <span
              v-else-if="selectedOrder.payment_missing"
              class="text-sm text-n-slate-10"
            >
              Ödeme kaydı yok — kâra dahil değil
            </span>
            <span
              v-else-if="selectedOrder.net_profit != null"
              class="text-lg font-bold tabular-nums"
              :class="
                selectedOrder.net_profit < 0
                  ? 'text-ruby-700 dark:text-ruby-400'
                  : 'text-green-700 dark:text-green-400'
              "
              >{{ money(selectedOrder.net_profit) }}</span
            >
            <span v-else class="text-sm text-yellow-600 dark:text-yellow-400">
              maliyet eksik
            </span>
          </div>
          <p class="text-[11px] text-n-slate-10">
            Net = Ciro − Ürün Maliyeti − Kargo (gidiş taşıma + kapıda komisyon +
            iade dönüş kargosu). Reklam ve altyapı giderleri sipariş bazında
            değil, dönem toplamında düşülür (üstteki gerçek kâr/zarar).
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
